/**
 * Inbox page with three-column layout: message list, conversation, and sender details
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { chatApi } from '@/api/chat';
import { useAuth } from '@/hooks/useAuth';
import type { Chat, ChatFilters } from '@/types/chat';
import { MessageList, ConversationView, ContactDetails, MobileAppBar, EmailNotificationForm } from '@/components/Inbox';
import { AiHelperFloatingButton } from '@/components/AiHelper';
import type { ChatResponse } from '@/types/chat';

interface InboxProps {}

interface FilterState {
  search: string;
  readStatus: 'all' | 'unread' | 'read' | 'archived' | 'important' | 'last_receiver';
  streams: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  messageCategories: string[];
  conversationSignals: string[];
  conversationState: string | null;
  owners: string[];
  tags: string[];
  lastSenders: string[];
}

export const Inbox: React.FC<InboxProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [view, setView] = useState<'list' | 'conversation' | 'details'>('list');
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    readStatus: 'all',
    streams: [],
    dateFrom: null,
    dateTo: null,
    messageCategories: [],
    conversationSignals: [],
    conversationState: null,
    owners: [],
    tags: [],
    lastSenders: [],
  });
  const [expandedFilters, setExpandedFilters] = useState(false);
  
  // Initialize activeTab from URL params, default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        return tabIndex;
      }
    }
    return 0;
  });
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [emailNotificationOpen, setEmailNotificationOpen] = useState(false);
  
  const queryClient = useQueryClient();

  // Get prospect or chat_uuid from URL params
  const prospectParam = searchParams.get('prospect');
  const chatUuidParam = searchParams.get('chat_uuid');

  // Convert filters to API format
  const apiFilters: ChatFilters = {
    search: filters.search || undefined,
    read_status: filters.readStatus === 'unread' ? 'unseen' : 
                 filters.readStatus === 'read' ? 'seen' : undefined,
    stream: filters.streams.length > 0 ? filters.streams : undefined, // Changed from campaigns to streams
    message_categories: filters.messageCategories.length > 0 ? filters.messageCategories : undefined,
    conversation_signals: filters.conversationSignals.length > 0 ? filters.conversationSignals : undefined,
    conversation_state: filters.conversationState || undefined,
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    operator: filters.owners.length > 0 ? filters.owners : undefined,
    last_sender: filters.lastSenders.length > 0 ? filters.lastSenders : undefined,
    date_from: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : undefined,
    date_to: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : undefined,
    // Only send is_archived/is_important/is_last_receiver when those specific categories are selected
    // and clear read_status to avoid conflicts
    ...(filters.readStatus === 'archived' && { 
      is_archived: true,
      read_status: undefined 
    }),
    ...(filters.readStatus === 'important' && { 
      is_important: true,
      read_status: undefined 
    }),
    ...(filters.readStatus === 'last_receiver' && { 
      is_last_receiver: true,
      read_status: undefined 
    }),
    // Add prospect or chat filter from URL params
    ...(prospectParam ? { prospect: prospectParam } : {}),
    ...(chatUuidParam ? { chat_uuid: chatUuidParam } : {}),
  };

  // Fetch chats with infinite scroll
  const {
    data: chatsPages,
    isLoading: isLoadingChats,
    error: chatsError,
    fetchNextPage: fetchNextChatsPage,
    hasNextPage: hasNextChatsPage,
    isFetchingNextPage: isFetchingNextChatsPage,
  } = useInfiniteQuery({
    queryKey: ['chats', apiFilters],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getChats({ page_size: 50, page_token: pageParam ?? undefined, ...apiFilters }),
    getNextPageParam: (lastPage: ChatResponse) => lastPage?.output?.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  // Auto-select chat if chat_uuid is provided
  useEffect(() => {
    if (chatUuidParam && chatsPages && !selectedChat) {
      const allChats = chatsPages.pages.flatMap((p: ChatResponse) => p.output.items);
      const targetChat = allChats.find((c: Chat) => c.key === chatUuidParam);
      if (targetChat) {
        setSelectedChat(targetChat);
        if (isMobile) {
          setView('conversation');
        }
      }
    }
  }, [chatUuidParam, chatsPages, selectedChat, isMobile]);

  // Fetch filter options - replaced campaigns with streams
  // Include user key in query key to ensure proper data refresh on impersonation
  const { data: streams = [] } = useQuery({
    queryKey: ['streams', user?.key],
    queryFn: chatApi.getStreams,
  });

  const { data: messageCategories = [] } = useQuery({
    queryKey: ['messageCategories'],
    queryFn: chatApi.getMessageCategories,
  });

  // Owners (users current user has access to for chats) - using infinite query to load all
  const {
    data: ownersPages,
    fetchNextPage: fetchNextOwnersPage,
    hasNextPage: hasNextOwnersPage,
    isFetchingNextPage: isFetchingNextOwnersPage,
  } = useInfiniteQuery({
    queryKey: ['chatOwners', user?.key],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!user,
  });

  // Flatten all owners from all pages
  const owners = (ownersPages?.pages.flatMap((p) => p.items) ?? []) as { uuid: string; name: string }[];

  // Load more owners when needed (auto-load all pages)
  useEffect(() => {
    if (hasNextOwnersPage && !isFetchingNextOwnersPage && user) {
      fetchNextOwnersPage();
    }
  }, [hasNextOwnersPage, isFetchingNextOwnersPage, fetchNextOwnersPage, user]);

  // Schedule message mutation
  const scheduleMessageMutation = useMutation({
    mutationFn: ({ chatId, content, scheduledAt }: { chatId: string; content: string; scheduledAt: Date }) =>
      chatApi.scheduleMessage(chatId, content, scheduledAt.toISOString()),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      setToast({
        open: true,
        message: 'Message scheduled successfully!',
        severity: 'success',
      });
    },
    onError: (error) => {
      console.error('Failed to schedule message:', error);
      setToast({
        open: true,
        message: 'Failed to schedule message. Please try again.',
        severity: 'error',
      });
    },
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: ({ chatId, message }: { chatId: string; message: string }) =>
      chatApi.sendReply(chatId, { content: message }),
    onSuccess: (updatedChat, { chatId }) => {
      // Only replace selected chat if the user is still on this conversation.
      if (updatedChat && selectedChat?.key === chatId) {
        setSelectedChat(updatedChat);
      }
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      queryClient.invalidateQueries({ queryKey: ['chat', chatId] });
      setToast({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });
    },
    onError: (error) => {
      console.error('Failed to send message:', error);
      setToast({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (chatId: string) => chatApi.markAsSeen(chatId),
    onMutate: async (chatId: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, seen_last_message: true }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _chatId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onSuccess: (updatedChat) => {
      // Update the selected chat with the new data
      if (selectedChat && updatedChat) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as important mutation
  const markAsImportantMutation = useMutation({
    mutationFn: ({ chatId, isImportant }: { chatId: string; isImportant: boolean }) => 
      chatApi.markAsImportant(chatId, isImportant),
    onMutate: async ({ chatId, isImportant }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, is_important: isImportant }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onSuccess: (updatedChat) => {
      // Update the selected chat with the new data
      if (selectedChat && updatedChat) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as archived mutation
  const markAsArchivedMutation = useMutation({
    mutationFn: ({ chatId, isArchived }: { chatId: string; isArchived: boolean }) => 
      chatApi.markAsArchived(chatId, isArchived),
    onMutate: async ({ chatId, isArchived }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, is_archived: isArchived }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onSuccess: (updatedChat) => {
      // Update the selected chat with the new data
      if (selectedChat && updatedChat) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as unread mutation
  const markAsUnreadMutation = useMutation({
    mutationFn: (chatId: string) => chatApi.markAsUnread(chatId),
    onMutate: async (chatId: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, seen_last_message: false }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _chatId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onSuccess: (updatedChat) => {
      // Update the selected chat with the new data
      if (selectedChat && updatedChat) {
        setSelectedChat(updatedChat);
      }
    },
  });

  const chats = (chatsPages as InfiniteData<ChatResponse> | undefined)?.pages.flatMap((p) => p.output.items) || [];

  // Handle chat selection
  const handleChatSelect = useCallback((chat: Chat) => {
    // If the chat is unread, optimistically update it as read immediately
    if (!chat.seen_last_message) {
      const updatedChat = { ...chat, seen_last_message: true };
      setSelectedChat(updatedChat);
      markAsReadMutation.mutate(chat.key);
    } else {
      setSelectedChat(chat);
    }
    
    if (isMobile) {
      setView('conversation');
    }
  }, [isMobile, markAsReadMutation]);

  // Handle send message
  const handleSendMessage = useCallback((message: string, chatId?: string) => {
    const targetChatId = chatId ?? selectedChat?.key;
    if (targetChatId && !sendMessageMutation.isPending) {
      sendMessageMutation.mutate({ chatId: targetChatId, message });
    }
  }, [selectedChat, sendMessageMutation]);

  // Handle schedule message
  const handleScheduleMessage = useCallback((message: string, scheduledAt: Date) => {
    if (selectedChat && !scheduleMessageMutation.isPending) {
      scheduleMessageMutation.mutate({
        chatId: selectedChat.key,
        content: message,
        scheduledAt,
      });
    }
  }, [selectedChat, scheduleMessageMutation]);

  // Handle mark as important
  const handleMarkImportant = useCallback((chatId: string, isImportant: boolean) => {
    markAsImportantMutation.mutate({ chatId, isImportant });
  }, [markAsImportantMutation]);

  // Handle mark as archived
  const handleMarkArchived = useCallback((chatId: string, isArchived: boolean) => {
    markAsArchivedMutation.mutate({ chatId, isArchived });
  }, [markAsArchivedMutation]);

  // Handle mark as unread
  const handleMarkUnread = useCallback((chatId: string) => {
    markAsUnreadMutation.mutate(chatId);
  }, [markAsUnreadMutation]);

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Update URL when tab changes
  const updateURLTab = useCallback((tabIndex: number) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (tabIndex === 0) {
      // Remove tab param if it's the default (first tab)
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabIndex.toString());
    }
    
    setSearchParams(newSearchParams, { replace: true });
    // Reset flag after a short delay to allow URL to update
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return; // Skip if this is from our own internal update
    }

    const tabParam = searchParams.get('tab');
    let urlTabIndex = 0;
    if (tabParam !== null) {
      const parsedTab = parseInt(tabParam, 10);
      if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 5) {
        urlTabIndex = parsedTab;
      }
    }

    if (urlTabIndex !== activeTab) {
      setActiveTab(urlTabIndex);
      // Also update the filter status to match the tab
      const statusMap = ['all', 'last_receiver', 'unread', 'read', 'important', 'archived'];
      setFilters(prev => ({ ...prev, readStatus: statusMap[urlTabIndex] as any }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const statusMap = ['all', 'last_receiver', 'unread', 'read', 'important', 'archived'];
    handleFilterChange('readStatus', statusMap[newValue] as any);
    updateURLTab(newValue);
  };

  // Mobile navigation
  const handleBack = () => {
    if (view === 'conversation') {
      setView('list');
      setSelectedChat(null);
    } else if (view === 'details') {
      setView('conversation');
    }
  };

  const handleNavigateToDetails = () => {
    if (isMobile) {
      setView('details');
    }
  };

  // Handle email notification
  const handleSendEmailNotification = () => {
    setEmailNotificationOpen(true);
  };

  const handleCloseEmailNotification = () => {
    setEmailNotificationOpen(false);
  };

  // Check if user is admin
  const isAdmin = user?.is_staff || user?.is_superuser;



  return (
    <Box sx={{ height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0, minHeight: 0 }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <MobileAppBar
          view={view}
          selectedChat={selectedChat}
          onBack={handleBack}
        />
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Desktop Layout */}
        {!isMobile && (
          <>
            {/* Left Section */}
            <Box sx={{ width: 380, borderRight: 1, borderColor: 'divider', height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0, flexShrink: 0 }}>
              <MessageList
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedChat={selectedChat}
                onChatSelect={handleChatSelect}
                chats={chats}
                isLoading={isLoadingChats}
                error={chatsError}
                fetchNextPage={fetchNextChatsPage}
                hasNextPage={hasNextChatsPage}
                isFetchingNextPage={isFetchingNextChatsPage}
                streams={streams}
                owners={owners}
                messageCategories={messageCategories}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={setExpandedFilters}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            </Box>

            {/* Middle Section */}
            <Box sx={{ flex: 1, minWidth: 0, borderRight: isTablet ? 0 : 1, borderColor: 'divider', height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0 }}>
              <ConversationView
                selectedChat={selectedChat}
                onSendMessage={handleSendMessage}
                onScheduleMessage={handleScheduleMessage}
                isSending={sendMessageMutation.isPending}
                onMarkImportant={handleMarkImportant}
                onMarkArchived={handleMarkArchived}
                onMarkUnread={handleMarkUnread}
                onNavigateToDetails={handleNavigateToDetails}
                onSendEmailNotification={handleSendEmailNotification}
                isAdmin={isAdmin}
                owners={owners}
                onChatUpdate={(updatedChat) => {
                  setSelectedChat(updatedChat);
                  queryClient.invalidateQueries({ queryKey: ['chats'] });
                }}
                onSendScheduledNowSuccess={() => {
                  setToast({ open: true, message: 'Message sent!', severity: 'success' });
                }}
                onSendScheduledNowError={() => {
                  setToast({ open: true, message: 'Failed to send message', severity: 'error' });
                }}
              />
            </Box>

            {/* Right Section */}
            {!isTablet && (
              <Box sx={{ width: 380, height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0, flexShrink: 0 }}>
                <ContactDetails selectedChat={selectedChat} />
              </Box>
            )}
          </>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <Box sx={{ flex: 1, height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0 }}>
            {view === 'list' && (
              <MessageList
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedChat={selectedChat}
                onChatSelect={handleChatSelect}
                chats={chats}
                isLoading={isLoadingChats}
                error={chatsError}
                fetchNextPage={fetchNextChatsPage}
                hasNextPage={hasNextChatsPage}
                isFetchingNextPage={isFetchingNextChatsPage}
                streams={streams}
                owners={owners}
                messageCategories={messageCategories}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={setExpandedFilters}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            )}
            {view === 'conversation' && (
              <ConversationView
                selectedChat={selectedChat}
                onSendMessage={handleSendMessage}
                onScheduleMessage={handleScheduleMessage}
                isSending={sendMessageMutation.isPending}
                onMarkImportant={handleMarkImportant}
                onMarkArchived={handleMarkArchived}
                onMarkUnread={handleMarkUnread}
                onNavigateToDetails={handleNavigateToDetails}
                onSendEmailNotification={handleSendEmailNotification}
                isAdmin={isAdmin}
                owners={owners}
                onChatUpdate={(updatedChat) => {
                  setSelectedChat(updatedChat);
                  queryClient.invalidateQueries({ queryKey: ['chats'] });
                }}
                onSendScheduledNowSuccess={() => {
                  setToast({ open: true, message: 'Message sent!', severity: 'success' });
                }}
                onSendScheduledNowError={() => {
                  setToast({ open: true, message: 'Failed to send message', severity: 'error' });
                }}
              />
            )}
            {view === 'details' && (
              <ContactDetails selectedChat={selectedChat} onBack={handleBack} />
            )}
          </Box>
        )}
      </Box>
      
      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* AI Helper - floating button when a chat is selected */}
      <AiHelperFloatingButton
        visible={!!selectedChat}
        context={
          selectedChat
            ? {
                chatUuid: selectedChat.key,
                prospectName: selectedChat.prospect?.full_name ?? undefined,
              }
            : null
        }
      />

      {/* Email Notification Form */}
      <EmailNotificationForm
        open={emailNotificationOpen}
        onClose={handleCloseEmailNotification}
        chat={selectedChat}
        currentUser={{
          name: user?.name || '',
          email: user?.email || '',
        }}
        onSuccess={(msg?: string) => {
          setToast({ open: true, message: msg || 'Email notification sent successfully', severity: 'success' });
        }}
      />
    </Box>
  );
}; 